import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../../environments/environment';
import { PermissionService } from './permission.service';
import { AuthSessionRedirectService } from '../auth/auth-session-redirect.service';

export type AdminNotificationPayload = {
  id: number;
  title: string;
  message: string;
  alert_type: string;
  is_read: boolean;
};

@Injectable({ providedIn: 'root' })
export class AdminWebSocketService implements OnDestroy {
  private readonly permissionService = inject(PermissionService);
  private readonly authSessionRedirect = inject(AuthSessionRedirectService);
  private readonly ngZone = inject(NgZone);
  private readonly wsBase =
    (environment as { wsBaseUrl?: string }).wsBaseUrl?.replace(/\/$/, '') ??
    environment.apiBaseUrl.replace(/\/api\/v1$/, '');

  private socket: Socket | null = null;
  private readonly notification$ = new Subject<AdminNotificationPayload>();

  readonly onNotification = this.notification$.asObservable();

  connect(authToken?: string, tenantId?: string): void {
    if (!authToken) {
      return;
    }
    if (this.socket?.connected) {
      return;
    }
    if (this.socket && !this.socket.connected) {
      this.socket.auth = { token: authToken, tenant: tenantId };
      this.socket.connect();
      return;
    }
    const wsOrigin =
      this.wsBase ||
      (globalThis.window === undefined ? '' : globalThis.location.origin);
    if (!wsOrigin) {
      return;
    }
    this.socket = io(wsOrigin, {
      path: '/socket.io/',
      withCredentials: true,
      // polling MUST come first so Socket.IO completes its HTTP handshake before upgrading
      // to WebSocket. Starting with 'websocket' skips the handshake and causes WS failures.
      transports: ['polling', 'websocket'],
      auth: {
        token: authToken,   // received by server via client.handshake.auth.token
        tenant: tenantId,
      },
      // NOTE: extraHeaders are intentionally omitted — browsers block custom headers
      // (Authorization, x-tenant-id) on WebSocket connections per spec. Use auth{} instead.
    });

    this.attachSocketHandlers();
  }

  private attachSocketHandlers(): void {
    if (!this.socket) {
      return;
    }

    this.socket.on('notification', (payload: AdminNotificationPayload) => {
      this.ngZone.run(() => this.notification$.next(payload));
    });

    this.socket.on(
      'permissions:updated',
      (_payload: { userId?: number; roleId?: number; reason?: string }) => {
        this.ngZone.run(() => {
          this.permissionService.refreshPermissions().subscribe();
        });
      },
    );

    this.socket.on(
      'session:revoked',
      (_payload: { reason?: string; message?: string }) => {
        this.ngZone.run(() => {
          this.disconnect();
          this.authSessionRedirect.redirectToLogin();
        });
      },
    );
  }

  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
  }

  ngOnDestroy(): void {
    this.disconnect();
    this.notification$.complete();
  }
}
